import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserCog, Trash2, Plus, FileUp } from "lucide-react";

// Pulls "NAME 123456789" pairs out of a GMIN list (PDF or pasted text).
export const parseGmins = (text) =>
  [...text.replace(/\s+/g, " ").matchAll(/([A-Za-z][A-Za-z .'-]{2,40}?)\s*[|:,-]?\s*(\d{9})\b/g)]
    .map((m) => ({ name: m[1].replace(/[|]/g, "").trim(), gmin: m[2] }));

export function ImportList({ store, onDone, path = "/gm/advisors", toBody = (r) => r }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const { pdfText } = await import("@/lib/pdfLibs");
      const rows = parseGmins(file.type === "application/pdf" || /\.pdf$/i.test(file.name) ? (await pdfText(file)).text : await file.text());
      if (!rows.length) { toast.error("No names with 9-digit GMINs found in that file"); return; }
      let added = 0, skipped = 0;
      for (const r of rows) {
        try { await api.post(path, { ...toBody(r), dealership_id: store }); added++; } catch { skipped++; }
      }
      toast.success(`Added ${added}${skipped ? ` - ${skipped} already saved` : ""}`);
      onDone();
    } catch (err) {
      toast.error(`Could not read that file: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.txt,.csv" hidden onChange={run} data-testid="gm-advisor-import-input" />
      <Button variant="outline" size="sm" className="gap-1" disabled={busy || !store} onClick={() => ref.current?.click()} data-testid="gm-advisor-import">
        <FileUp className="h-3.5 w-3.5" /> {busy ? "Importing..." : "Import GMIN list (PDF)"}
      </Button>
    </>
  );
}

export function useStores(key = "gm_store") {
  const [stores, setStores] = useState([]);
  const [store, setStoreState] = useState(localStorage.getItem(key) || "");
  useEffect(() => {
    api.get("/dealerships").then(({ data }) => {
      setStores(data);
      setStoreState((cur) => (data.some((s) => s.id === cur) ? cur : data[0]?.id || ""));
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setStore = (id) => { localStorage.setItem(key, id); setStoreState(id); };
  return { stores, store, setStore };
}

export function useAdvisors(enabled) {
  const [advisors, setAdvisors] = useState([]);
  const load = () => api.get("/gm/advisors").then(({ data }) => setAdvisors(data)).catch(() => {});
  useEffect(() => { if (enabled) load(); }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return [advisors, load];
}

export const StoreSelect = ({ stores, store, setStore }) => (
  <select value={store} onChange={(e) => setStore(e.target.value)} data-testid="gm-store-select"
    className="h-9 rounded-md border border-white/30 bg-white/10 px-2 text-sm text-white [&>option]:text-black">
    {stores.length === 0 && <option value="">No stores yet</option>}
    {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
  </select>
);

export function AdvisorRoster({ advisors, onChange, store, storeName }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", number: "", gmin: "" });
  const mine = advisors.filter((a) => a.dealership_id === store);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const add = async () => {
    try {
      await api.post("/gm/advisors", { ...f, dealership_id: store });
      setF({ name: "", number: "", gmin: "" });
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add advisor");
    }
  };
  const remove = async (id) => { await api.delete(`/gm/advisors/${id}`); onChange(); };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} disabled={!store} data-testid="gm-advisors-button" className="gap-2 border-white/30 bg-white/10 text-white hover:bg-white/20">
        <UserCog className="h-4 w-4" /> Advisors ({mine.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="gm-advisors-dialog">
          <DialogHeader>
            <DialogTitle>Service advisors - {storeName}</DialogTitle>
            <DialogDescription>Save each advisor's GMIN once. The AI matches the advisor printed on the RO and fills the GMIN on every draft for this store.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            <Input placeholder="Advisor name" value={f.name} onChange={set("name")} className="min-w-[140px] flex-1" data-testid="gm-advisor-name" />
            <Input placeholder="# on RO" value={f.number} onChange={set("number")} className="w-24" data-testid="gm-advisor-number" />
            <Input placeholder="GMIN" value={f.gmin} onChange={set("gmin")} className="w-32" data-testid="gm-advisor-gmin" />
            <Button onClick={add} disabled={!f.name.trim() || !f.gmin.trim()} data-testid="gm-advisor-add"><Plus className="h-4 w-4" /></Button>
          </div>
          <ImportList store={store} onDone={onChange} />
          <ul className="mt-2 divide-y divide-border rounded-md border border-border">
            {mine.length === 0 && <li className="p-3 text-sm text-muted-foreground">No advisors for this store yet.</li>}
            {mine.map((a) => (
              <li key={a.id} className="flex items-center justify-between p-2.5 text-sm" data-testid={`gm-advisor-${a.id}`}>
                <span>{a.name} {a.number && <span className="text-muted-foreground">#{a.number}</span>} <span className="font-mono text-muted-foreground">GMIN {a.gmin}</span></span>
                <Button variant="ghost" size="icon" onClick={() => remove(a.id)} data-testid={`gm-advisor-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
